<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow :name="$t('document-type.documentSeries')" mode="edit">
          <BaseSelectDropdown
            :items="dropdownDocumentSeries"
            :selected-item="selectedDocumentSeries"
            @update:selected="handleDocumentSeriesSelection"
            name="documentSeries"
            show-error-as-tooltip
            clearable
            :error="documentSeriesErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow :name="$t('document-type.defaultWarehouse')" mode="edit">
          <BaseSelectDropdown
            :items="dropdownWarehouses"
            :selected-item="selectedWarehouse"
            @update:selected="handleWarehouseSelection"
            name="defaultWarehouse"
            mode="edit"
            clearable />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow :name="$t('document-type.sequenceFormat')" mode="edit">
          <BaseInput v-model="data.sequenceFormat" color="base" flat />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import { onMounted, ref } from 'vue';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import type { SelectDropdownItem } from '@/types/Base';
import type { DocumentSeriesType } from '@/types/DocumentSeriesTypes';

defineProps<{
  documentSeriesErrorMessage: string;
}>();

const { getDocumentSeriesLookup, getWarehousesLookup } = useLookupsStore();

const { dropdownDocumentSeries, documentSeries, dropdownWarehouses, warehouses } =
  storeToRefs(useLookupsStore());

const selectedDocumentSeries = ref<SelectDropdownItem | null>(null);
const selectedWarehouse = ref<SelectDropdownItem | null>(null);

function handleDocumentSeriesSelection(item: SelectDropdownItem) {
  if (!data.value) return;
  selectedDocumentSeries.value = item ? item : null;

  data.value.documentSeriesID = item ? item.value.toString() : '';
  const existingDocumentSeries = item
    ? documentSeries.value.find((s) => s.id === item.value.toString())
    : undefined;
  if (existingDocumentSeries) {
    data.value.documentSeries = {
      id: existingDocumentSeries.id,
      name: existingDocumentSeries.name || '',
      code: existingDocumentSeries.code || '',
    };
  }
}

function handleWarehouseSelection(item: SelectDropdownItem | null) {
  if (!data.value) return;
  selectedWarehouse.value = item;

  if (item === null) {
    data.value.defaultWarehouseID = undefined;
    data.value.defaultWarehouse = undefined;
  } else {
    data.value.defaultWarehouseID = item.value.toString();
    const existingWarehouse = warehouses.value.find((x) => x.id === item.value.toString());
    if (existingWarehouse) {
      data.value.defaultWarehouse = {
        id: existingWarehouse.id,
        name: existingWarehouse.name,
        code: existingWarehouse.code,
      };
    }
  }
}

const data = defineModel<DocumentSeriesType>();

function initDropdowns() {
  if (data.value) {
    const documentSeriesTypesValue = dropdownDocumentSeries.value.find(
      (item) => item.value == data.value?.documentSeriesID
    );
    if (documentSeriesTypesValue) selectedDocumentSeries.value = documentSeriesTypesValue;

    const warehouseValue = dropdownWarehouses.value.find(
      (item) => item.value == data.value?.defaultWarehouseID
    );
    if (warehouseValue) selectedWarehouse.value = warehouseValue;
  }
}

onMounted(async () => {
  documentSeries.value = [];
  warehouses.value = [];

  await getDocumentSeriesLookup();
  await getWarehousesLookup();
  initDropdowns();
});
</script>
